import React, { useState, useEffect } from 'react';
import axios from 'axios';

const ADMIN_TOKEN_KEY = 'metalbox_admin_token';

const StoriesSettings = () => {
    const [settings, setSettings] = useState({
        background_image: '',
        hero_title_zh: '',
        hero_title_en: '',
        hero_subtitle_zh: '',
        hero_subtitle_en: '',
    });
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState({ type: '', text: '' });

    // 获取当前设置
    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const response = await axios.get(`${window.location.origin}/api/stories-settings`);
            if (response.data.success) {
                setSettings(response.data.settings);
            }
        } catch (error) {
            console.error('Failed to fetch settings:', error);
            showMessage('error', '获取设置失败');
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // 验证文件类型
        if (!file.type.startsWith('image/')) {
            showMessage('error', '请选择图片文件');
            return;
        }

        // 验证文件大小 (30MB)
        if (file.size > 30 * 1024 * 1024) {
            showMessage('error', '图片大小不能超过30MB');
            return;
        }

        setUploading(true);
        const formData = new FormData();
        formData.append('image', file);

        try {
            const token = localStorage.getItem(ADMIN_TOKEN_KEY);
            const response = await axios.post(
                `${window.location.origin}/api/admin/stories-settings/upload-background`,
                formData,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Content-Type': 'multipart/form-data',
                    },
                }
            );

            if (response.data.success) {
                const newImagePath = response.data.imagePath;
                setSettings(prev => ({
                    ...prev,
                    background_image: newImagePath
                }));

                // 自动保存到数据库
                try {
                    await axios.put(
                        `${window.location.origin}/api/admin/stories-settings`,
                        { ...settings, background_image: newImagePath },
                        {
                            headers: {
                                'Authorization': `Bearer ${token}`,
                            },
                        }
                    );
                    showMessage('success', '背景图片上传并保存成功');
                } catch (saveError) {
                    console.error('Auto-save error:', saveError);
                    showMessage('success', '背景图片上传成功，但自动保存失败，请手动点击保存按钮');
                }
            }
        } catch (error) {
            console.error('Upload error:', error);
            showMessage('error', error.response?.data?.message || '图片上传失败');
        } finally {
            setUploading(false);
        }
    };

    const handleSave = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem(ADMIN_TOKEN_KEY);
            const response = await axios.put(
                `${window.location.origin}/api/admin/stories-settings`,
                settings,
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );

            if (response.data.success) {
                showMessage('success', 'Stories设置保存成功');
            }
        } catch (error) {
            console.error('Save error:', error);
            showMessage('error', error.response?.data?.message || '保存失败');
        } finally {
            setLoading(false);
        }
    };

    const showMessage = (type, text) => {
        setMessage({ type, text });
        setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    };

    const getImageUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        // 使用主站域名而不是admin子域名
        const mainDomain = window.location.origin.replace('admin.', '');
        return `${mainDomain}${path.startsWith('/') ? '' : '/'}${path}`;
    };

    return (
        <div className="p-6">
            <h2 className="text-3xl font-bold mb-6 text-white">Stories页面设置</h2>

            {/* 消息提示 */}
            {message.text && (
                <div className={`mb-6 p-4 rounded-lg border ${message.type === 'success'
                    ? 'bg-green-500/20 text-green-400 border-green-500/50'
                    : 'bg-red-500/20 text-red-400 border-red-500/50'
                    }`}>
                    {message.text}
                </div>
            )}

            {/* 背景图片 */}
            <div className="mb-8 bg-[#1a1d17] border border-[#2a2d27] p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 text-white">背景图片</h3>

                {settings.background_image && (
                    <div className="mb-4">
                        <img
                            src={getImageUrl(settings.background_image)}
                            alt="Background"
                            className="w-full max-h-96 object-cover rounded-lg border border-[#2a2d27]"
                        />
                    </div>
                )}

                <div className="flex items-center gap-4">
                    <label className={`cursor-pointer px-4 py-2 rounded-lg transition-colors flex items-center gap-2 ${uploading
                            ? 'bg-[#2a2d27] text-gray-400 cursor-not-allowed'
                            : 'bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white'
                        }`}>
                        {uploading ? (
                            <>
                                <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                上传中...
                            </>
                        ) : (
                            <>
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
                                    <path d="M216,40H40A16,16,0,0,0,24,56V200a16,16,0,0,0,16,16H216a16,16,0,0,0,16-16V56A16,16,0,0,0,216,40Zm0,16V158.75l-26.07-26.06a16,16,0,0,0-22.63,0l-20,20-44-44a16,16,0,0,0-22.62,0L40,149.37V56ZM40,172l52-52,80,80H40Zm176,28H194.63l-36-36,20-20L216,181.38V200ZM144,100a12,12,0,1,1,12,12A12,12,0,0,1,144,100Z"></path>
                                </svg>
                                上传新背景图片
                            </>
                        )}
                        <input
                            type="file"
                            accept="image/*"
                            onChange={handleImageUpload}
                            disabled={uploading}
                            className="hidden"
                        />
                    </label>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                    支持 JPG, PNG, GIF, WebP 格式，最大30MB。图片将自动压缩为WebP格式。
                </p>
            </div>

            {/* Hero标题 */}
            <div className="mb-8 bg-[#1a1d17] border border-[#2a2d27] p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 text-white">Hero标题</h3>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                        中文标题
                    </label>
                    <input
                        type="text"
                        name="hero_title_zh"
                        value={settings.hero_title_zh}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                        placeholder="例如：我们的故事"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                        英文标题
                    </label>
                    <input
                        type="text"
                        name="hero_title_en"
                        value={settings.hero_title_en}
                        onChange={handleInputChange}
                        className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                        placeholder="e.g., Our Stories"
                    />
                </div>
            </div>

            {/* Hero副标题 */}
            <div className="mb-8 bg-[#1a1d17] border border-[#2a2d27] p-6 rounded-lg">
                <h3 className="text-xl font-semibold mb-4 text-white">Hero副标题</h3>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                        中文副标题
                    </label>
                    <textarea
                        name="hero_subtitle_zh"
                        value={settings.hero_subtitle_zh}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                        placeholder="例如：探索我们的参展历程与影视剧合作"
                    />
                </div>

                <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-400 mb-2">
                        英文副标题
                    </label>
                    <textarea
                        name="hero_subtitle_en"
                        value={settings.hero_subtitle_en}
                        onChange={handleInputChange}
                        rows="3"
                        className="w-full px-4 py-2 bg-[#1a1d17] border border-[#2a2d27] rounded-lg text-white focus:outline-none focus:border-[#f5a623]"
                        placeholder="e.g., Explore our exhibition journey and film collaborations"
                    />
                </div>
            </div>

            {/* 保存按钮 */}
            <div className="flex justify-end">
                <button
                    onClick={handleSave}
                    disabled={loading}
                    className="bg-[#4a5f3a] hover:bg-[#5a6f4a] text-white px-8 py-3 rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                    {loading ? (
                        <>
                            <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            保存中...
                        </>
                    ) : (
                        <>
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 256 256">
                                <path d="M229.66,49.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,164.69,218.34,38.34a8,8,0,0,1,11.32,11.32Z"></path>
                            </svg>
                            保存更改
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default StoriesSettings;
